import { ExportOutlined } from '@ant-design/icons';
import { getLowerCaseLanguageCode } from '@mahawi/eshop-common/src/translation';
import {
  type ILanguageType,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import { Alert, Collapse, Divider, Skeleton, Space } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import ChildCategories from '../components/category/child-categories';
import ComparisonPortalsCategoriesComponent from '../components/category/comparison-portals-categories';
import EbayCategoriesComponent from '../components/category/ebay-categories';
import Name from '../components/category/name';
import PricesComponent from '../components/category/prices';
import ProductsComponent from '../components/category/products';
import PropertiesComponent from '../components/category/properties';
import SeoComponent from '../components/category/seo';
import SlugComponent from '../components/category/slug';
import CategoryWebAnalytics from '../components/category/web-analytics';
import DetailPageHeader from '../components/detail-page-header';
import LanguageButtons from '../components/language-buttons';
import LinkButton from '../components/link-button';
import SectionLabel from '../components/section-label';
import StatusIcon from '../components/status-icon';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { categoriesLoadAll } from '../reducers/categories/reducer';
import { categoryLoad } from '../reducers/category/reducer';
import {
  loadComparisonPortalAll,
  loadComparisonPortalCategoriesAll,
} from '../reducers/comparison-portal/reducer';
import { ebayMarketplacesLoad } from '../reducers/ebay/reducer';
import { productsLoadByCategory } from '../reducers/products/reducer';
import { propertiesLoad } from '../reducers/properties/reducer';
import { SPACING } from '../theme/tokens';

const getDirectTranslation = (
  translations: ITranslation[] | undefined,
  lang?: ILanguageType,
): string | null =>
  translations?.find(({ code }: ITranslation): boolean => code === lang?.code)
    ?.value ?? null;

function CategoryContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const categoryCategory = useSelector((s: RootState) => s.Category.category);
  const categoryInProcess = useSelector((s: RootState) => s.Category.inProcess);
  const categoryUpdatedAt = useSelector((s: RootState) => s.Category.updatedAt);
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const languages = useSelector((s: RootState) => s.Language.languages);
  const configUrl = useSelector((s: RootState) => s.Config.url);

  const { categoryUUID } = useParams();

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const categoryNameLocalized = useMemo(
    () =>
      categoryCategory
        ? getDirectTranslation(categoryCategory.names, languageType) ||
          EMPTY_VALUE
        : '** NOT YET SET **',
    [categoryCategory, languageType],
  );

  const nameInvalidCodes = useMemo((): Set<string> => {
    if (!languages || !categoryCategory) return new Set();
    const invalid = new Set<string>();
    languages.forEach((lang: ILanguageType): void => {
      const n = getDirectTranslation(categoryCategory.names, lang);
      if (!n || n.length <= 2) invalid.add(lang.code);
    });
    return invalid;
  }, [languages, categoryCategory?.names, categoryUpdatedAt]);

  const slugInvalidCodes = useMemo((): Set<string> => {
    if (!languages || !categoryCategory) return new Set();
    const invalid = new Set<string>();
    languages.forEach((lang: ILanguageType): void => {
      const s = getDirectTranslation(categoryCategory.slugs, lang);
      if (!s || s.length < 2) invalid.add(lang.code);
    });
    return invalid;
  }, [languages, categoryCategory?.slugs, categoryUpdatedAt]);

  const seoInvalidCodes = useMemo((): Set<string> => {
    if (!languages || !categoryCategory) return new Set();
    const invalid = new Set<string>();
    languages.forEach((lang: ILanguageType): void => {
      const d = getDirectTranslation(categoryCategory.metaDescriptions, lang);
      if (!d || d.length < 2) invalid.add(lang.code);
    });
    return invalid;
  }, [languages, categoryCategory?.metaDescriptions, categoryUpdatedAt]);

  const childCategoriesLength =
    categoryCategory?.childCategoriesUUIDs.length ?? 0;

  useEffect((): void => {
    if (categoryUUID) {
      dispatch(categoryLoad(categoryUUID));
    }
  }, [dispatch, categoryUUID]);

  const handleCollapseChange = useCallback(
    (keys: string | string[]): void => {
      const keysArr = Array.isArray(keys) ? keys : [keys];
      const newlyOpened = keysArr.filter((k) => !openKeys.includes(k));
      setOpenKeys(keysArr);

      if (newlyOpened.includes('properties')) dispatch(propertiesLoad());
      if (newlyOpened.includes('child-categories'))
        dispatch(categoriesLoadAll());
      if (newlyOpened.includes('ebay')) dispatch(ebayMarketplacesLoad());
      if (newlyOpened.includes('comparison-portals')) {
        dispatch(loadComparisonPortalAll());
        dispatch(loadComparisonPortalCategoriesAll());
      }
      if (newlyOpened.includes('products') && categoryUUID) {
        dispatch(productsLoadByCategory(categoryUUID));
      }
    },
    [dispatch, openKeys, categoryUUID],
  );

  if (categoryCategory === undefined || !categoryUUID) {
    return <Skeleton active />;
  }

  if (categoryCategory === null) {
    return (
      <Alert
        type="error"
        title="Category not found"
        description={`Category UUID: ${categoryUUID}`}
        showIcon
      />
    );
  }

  return (
    <>
      <DetailPageHeader
        title={categoryNameLocalized}
        onReload={(): void => {
          dispatch(categoryLoad(categoryUUID));
        }}
        isReloadDisabled={categoryInProcess}
        uuid={categoryUUID}
        links={
          categoryCategory.parentCategoryUUID && (
            <LinkButton
              to={`/category/${categoryCategory.parentCategoryUUID}`}
              label="Open parent category"
              icon={<ExportOutlined />}
              openInNewTab
              isIconOnly
            />
          )
        }
        externalUrl={`${configUrl}/${getLowerCaseLanguageCode(languageType)}/category/${categoryUUID}`}
      />

      <LanguageButtons />

      <Divider />

      <Collapse
        onChange={handleCollapseChange}
        items={[
          {
            key: 'name',
            label: (
              <Space size={SPACING.sm} align="start">
                <strong>Category names</strong>
                <StatusIcon ok={nameInvalidCodes.size === 0} />
                {nameInvalidCodes.size > 0 && (
                  <small>{[...nameInvalidCodes].join(', ')}</small>
                )}
              </Space>
            ),
            children: <Name />,
          },
          {
            key: 'slug',
            label: (
              <Space size={SPACING.sm} align="start">
                <strong>Category slugs</strong>
                <StatusIcon ok={slugInvalidCodes.size === 0} />
                {slugInvalidCodes.size > 0 && (
                  <small>{[...slugInvalidCodes].join(', ')}</small>
                )}
              </Space>
            ),
            children: <SlugComponent />,
          },
          {
            key: 'seo',
            label: (
              <Space size={SPACING.sm} align="start">
                <strong>SEO</strong>
                <StatusIcon ok={seoInvalidCodes.size === 0} />
                {seoInvalidCodes.size > 0 && (
                  <small>{[...seoInvalidCodes].join(', ')}</small>
                )}
              </Space>
            ),
            children: <SeoComponent />,
          },
          {
            key: 'properties',
            label: <SectionLabel title="Properties" />,
            children: <PropertiesComponent />,
          },
          {
            key: 'child-categories',
            label: (
              <Space size={SPACING.sm} align="start">
                <strong>{`Child categories (${childCategoriesLength})`}</strong>
                <StatusIcon ok={childCategoriesLength > 0} />
              </Space>
            ),
            children: <ChildCategories />,
          },
          {
            key: 'ebay',
            label: <SectionLabel title="eBay categories" />,
            children: <EbayCategoriesComponent />,
          },
          {
            key: 'comparison-portals',
            label: <SectionLabel title="Comparison portals" />,
            children: <ComparisonPortalsCategoriesComponent />,
          },
          {
            key: 'prices',
            label: <SectionLabel title="Prices" />,
            children: <PricesComponent />,
          },
          {
            key: 'web-analytics',
            label: <SectionLabel title="Web analytics" />,
            children: <CategoryWebAnalytics categoryUUID={categoryUUID} />,
          },
          {
            key: 'products',
            label: <SectionLabel title="Products" />,
            children: <ProductsComponent />,
          },
        ]}
      />
    </>
  );
}

export default CategoryContainer;
